<template>
  <div class="goods-container">
    <!-- ===== 筛选卡片 ===== -->
    <el-card class="filter-card" shadow="never">
      <el-form :inline="true" class="filter-form" size="medium">
        <el-form-item label="货品名称">
          <el-input
            v-model="searchKeyword"
            placeholder="请输入货品名称"
            clearable
            @clear="clearSearch"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item label="分组">
          <el-select
            v-model="currentGroupId"
            placeholder="选择分组"
            clearable
            @change="fetchGoodsData"
            style="width: 180px;"
          >
            <el-option
              v-for="g in groupList"
              :key="g.id"
              :label="g.name"
              :value="g.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="clearSearch">重置</el-button>
          <el-button plain @click="openFilterPopup">
            <i class="el-icon-s-operation"></i> 筛选
          </el-button>
          <el-button type="success" @click="handleAdd">
            <i class="el-icon-plus"></i> 新建货品
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- ===== 表格卡片 ===== -->
    <el-card class="table-card" shadow="never">
      <el-table
        v-loading="loading"
        :data="goodsList"
        border
        stripe
        height="59vh"
        style="width: 100%"
        @row-click="showGoodsDetail"
      >
        <!-- 名称 -->
        <el-table-column prop="comlist_v1" label="名称" min-width="140" show-overflow-tooltip />

        <!-- 图片 -->
        <el-table-column label="图片" width="200" align="center">
          <template slot-scope="scope">
            <el-image
              v-if="scope.row.goods_img"
              :src="scope.row.goods_img"
              fit="cover"
              :preview-src-list="[scope.row.goods_img]"
              @click.stop=""
              style="width: 50px; height: 50px; border-radius: 4px;"
            />
            <i v-else class="el-icon-picture-outline" style="font-size: 32px; color: #ccc;"></i>
          </template>
        </el-table-column>

        <!-- 上下架 -->
        <el-table-column label="上下架" width="200" align="center">
          <template slot-scope="scope">
            <el-tag :type="getOnlineStatusClass(scope.row) === 'online-status' ? 'success' : 'danger'" size="small">
              {{ getOnlineStatusText(scope.row) }}
            </el-tag>
          </template>
        </el-table-column>

        <!-- 规格 -->
        <el-table-column label="规格" width="200" align="center">
          <template slot-scope="scope">
            <el-tag :type="getSpecTypeClass(scope.row) === 'multi-spec' ? 'warning' : 'info'" size="small">
              {{ getSpecTypeText(scope.row) }}
            </el-tag>
          </template>
        </el-table-column>

        <!-- 分类 -->
        <el-table-column prop="comlist_v5" label="货品分类" width="120" show-overflow-tooltip />

        <!-- 创建时间 -->
        <el-table-column prop="comlist3" label="创建时间" width="240" />

        <!-- 总库存 -->
        <el-table-column prop="com_all" label="总库存" width="100" align="center" />

        <!-- 上架库存 -->
        <el-table-column prop="shanjianum" label="上架库存" width="100" align="center" />

        <!-- 操作 -->
        <el-table-column label="操作" width="250" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click.stop="editGoods(scope.row.id)">编辑</el-button>
            <el-button size="mini" type="danger" @click.stop="deleteGoods(scope.row.id)">删除</el-button>
          </template>
        </el-table-column>

        <!-- 空状态插槽 -->
        <template slot="empty">
          <div style="padding: 40px 0; color: #999;">
            <i class="el-icon-document" style="font-size: 48px; display: block; margin-bottom: 10px;"></i>
            暂无货品数据
          </div>
        </template>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        class="pagination"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="totalItems"
        background
      />
    </el-card>

    <!-- ===== 商品详情弹窗 ===== -->
    <el-dialog
      title="货品详情"
      :visible.sync="showDetailDialog"
      width="700px"
      :close-on-click-modal="false"
      @close="closeDetail"
    >
      <div v-if="currentGoods && currentGoods.id">
        <div class="detail-header">
          <div class="detail-title-row">
            <h4 color="#333">{{ currentGoods.comlist_v1 || '未命名商品' }}</h4>
            <div>
              <el-tag size="small" :type="getOnlineStatusClass(currentGoods) === 'online-status' ? 'success' : 'danger'">
                {{ getOnlineStatusText(currentGoods) }}
              </el-tag>
              <el-tag size="small" :type="getSpecTypeClass(currentGoods) === 'multi-spec' ? 'warning' : 'info'">
                {{ getSpecTypeText(currentGoods) }}
              </el-tag>
            </div>
          </div>
          <el-divider></el-divider>
        </div>
        <div class="detail-body">
          <el-row :gutter="20">
            <el-col :span="12"><span class="label">货品分类：</span>{{ currentGoods.comlist_v5 || '未分类' }}</el-col>
            <el-col :span="12"><span class="label">创建时间：</span>{{ currentGoods.comlist3 || '2023-06-15' }}</el-col>
          </el-row>
          <el-row :gutter="20" style="margin-top: 12px;">
            <el-col :span="12"><span class="label">总库存：</span>{{ currentGoods.com_all || 0 }} 件</el-col>
            <el-col :span="12"><span class="label">上架库存：</span>{{ currentGoods.shanjianum || 0 }} 件</el-col>
          </el-row>

          <!-- 规格列表 -->
          <div class="spec-section" v-if="currentGoods.goods_mx && currentGoods.goods_mx.length">
            <h4>规格明细</h4>
            <el-table :data="currentGoods.goods_mx" border stripe size="small">
              <el-table-column prop="comlist_v1" label="规格名称" min-width="120"></el-table-column>
              <el-table-column prop="comlist_s1" label="数量" width="100"></el-table-column>
              <!-- <el-table-column prop="comlist_s1_1" label="上架库存" width="100"></el-table-column> -->
              <el-table-column label="状态" width="100">
                <template slot-scope="scope">
                  <el-tag :type="scope.row.status === '上架' ? 'success' : 'danger'" size="mini">
                    {{ scope.row.status }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="200">
                <template slot-scope="scope">
                  <el-button
                    size="mini"
                    :type="scope.row.status === '上架' ? 'warning' : 'success'"
                    @click.stop="toggleSpecStatus(scope.row)"
                  >
                    {{ scope.row.status === '上架' ? '下架' : '上架' }}
                  </el-button>
                  <el-button size="mini" type="danger" @click.stop="delSpecStatus(scope.row)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
          <div v-else class="no-spec">暂无规格</div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="closeDetail">关闭</el-button>
        <el-button type="primary" @click="editGoods(currentGoods.id)">编辑</el-button>
        <el-button type="danger" @click="deleteGoods(currentGoods.id)">删除</el-button>
      </span>
    </el-dialog>

    <!-- ===== 筛选弹窗 ===== -->
    <el-dialog title="商品筛选" :visible.sync="showFilterDialog" width="600px" :close-on-click-modal="false">
      <el-form label-width="100px" size="small">
        <!-- 省市区 -->
        <el-form-item label="所在地区">
          <el-select v-model="selectedProvinceId" placeholder="请选择省" @change="onProvinceChange" clearable>
            <el-option
              v-for="item in provinceList"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            ></el-option>
          </el-select>
          <el-select v-model="selectedCityId" placeholder="请选择市" @change="onCityChange" clearable :disabled="!selectedProvinceId" style="margin-left: 10px;">
            <el-option
              v-for="item in cityList"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            ></el-option>
          </el-select>
          <el-select v-model="selectedDistrictId" placeholder="请选择区" clearable :disabled="!selectedCityId" style="margin-left: 10px;">
            <el-option
              v-for="item in districtList"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            ></el-option>
          </el-select>
        </el-form-item>

        <!-- 商品标签（多选） -->
        <el-form-item label="商品标签">
          <el-select v-model="selectedTagIds" multiple placeholder="请选择商品标签" clearable>
            <el-option
              v-for="item in goodsTypeList"
              :key="item.id"
              :label="item.bfenlei4"
              :value="item.id"
            ></el-option>
          </el-select>
        </el-form-item>

        <!-- 商品状态 -->
        <el-form-item label="商品状态">
          <el-radio-group v-model="filterStatus">
            <el-radio label="">全部</el-radio>
            <el-radio label="上架">上架</el-radio>
            <el-radio label="下架">下架</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="resetFilter">重置</el-button>
        <el-button type="primary" @click="confirmFilter">确认筛选</el-button>
      </span>
    </el-dialog>

    <!-- ===== 商品表单弹窗 ===== -->
    <goods-form-dialog
      :visible.sync="formDialogVisible"
      :mode="formMode"
      :goods-id="editingGoodsId"
      @success="onFormSuccess"
    />
  </div>
</template>

<script>
import request from '@/utils/request'
import xcxrequest from '@/utils/xcxrequest'
import GoodsFormDialog from './GoodsFormDialog.vue'

export default {
  components: { GoodsFormDialog },
  name: 'GoodsLibrary',
  data () {
    return {
      // ---- 搜索 ----
      searchKeyword: '',
      // ---- 加载 ----
      loading: false,
      // ---- 分组 ----
      groupList: [],
      currentGroup: { id: '', name: '全部分组' },
      currentGroupId: null,
      // ---- 商品列表 ----
      goodsList: [],
      // ---- 详情弹窗 ----
      showDetailDialog: false,
      currentGoods: {},
      // ---- 筛选弹窗 ----
      showFilterDialog: false,
      // 省市区
      provinceList: [],
      cityList: [],
      districtList: [],
      selectedProvinceId: null,
      selectedCityId: null,
      selectedDistrictId: null,
      // 标签
      goodsTypeList: [],
      selectedTagIds: [],
      // 品牌
      brandList: [],
      selectedBrandId: null,
      selectedBrandName: '',
      // 状态
      filterStatus: '',
      // 筛选模式
      isFilterMode: false,
      filterParams: {},
      // ---- 表单弹窗 ----
      formDialogVisible: false,
      formMode: 'add', // 'add' | 'edit'
      editingGoodsId: null,
      // ---- 分页 ----
      currentPage: 1,
      pageSize: 10,
      totalPages: 0
    }
  },
  computed: {
    totalItems () {
      return this.totalPages * this.pageSize
    }
  },
  mounted () {
    this.fetchGroupList().then(() => {
      this.fetchGoodsData()
    })
    this.loadProvinceList()
    this.loadTagsAndBrands()
  },
  methods: {
    // ============================
    // 分组
    // ============================
    async fetchGroupList () {
      try {
        const shopId = localStorage.getItem('sh_id') || '2'
        const params = { bfenlei1: shopId }
        const res = await request.post('/goods/fenzu_jiben_list.php', params)
        if (res && res.code === 0 && Array.isArray(res.fenzuList)) {
          this.groupList = res.fenzuList.map(item => ({
            id: item.id,
            name: item.bfenlei4
          }))
          // if (this.groupList.length > 0) {
          //   this.currentGroupId = this.groupList[0].id
          // }
        }
      } catch (error) {
        console.error('获取分组列表失败', error)
      }
    },

    // ============================
    // 商品数据（支持分页）
    // ============================
    async fetchGoodsData (keepPage = false) {
      if (!keepPage) {
        this.currentPage = 1
      }
      this.loading = true
      try {
        const shopId = localStorage.getItem('sh_id') || '2'
        const userType = localStorage.getItem('user_type') || ''
        let url; const params = {
          sh_id: shopId,
          user_type: userType,
          page: this.currentPage,
          page_size: this.pageSize
        }

        if (this.isFilterMode && Object.keys(this.filterParams).length) {
          url = '/goods/all_goods_list.php'
          Object.assign(params, this.filterParams)
        } else {
          url = '/goods/fenzu_goods_list.php'
          params.comlist_v5_1 = this.currentGroupId
        }

        if (this.searchKeyword && this.searchKeyword.trim()) {
          params.goods_name = this.searchKeyword.trim()
        }

        const res = await request.post(url, params)
        if (res && res.code === 0 && Array.isArray(res.fzgoodsList)) {
          this.goodsList = res.fzgoodsList
          this.goodsList.forEach(item => {
            item.shanjianum = item.goods_mx
              ? item.goods_mx.reduce((total, mx) => total + parseInt(mx.comlist_s1_1 || 0), 0)
              : 0
          })
          this.totalPages = parseInt(res.pages) / this.pageSize || 1
        } else {
          this.goodsList = []
          this.totalPages = 0
        }
      } catch (error) {
        console.error('获取货品数据失败', error)
        this.goodsList = []
        this.totalPages = 0
      } finally {
        this.loading = false
      }
    },

    // ============================
    // 搜索
    // ============================
    handleSearch () {
      this.isFilterMode = false
      this.filterParams = {}
      this.fetchGoodsData()
    },
    clearSearch () {
      this.searchKeyword = ''
      this.fetchGoodsData()
    },

    // ============================
    // 筛选
    // ============================
    openFilterPopup () {
      this.showFilterDialog = true
    },
    resetFilter () {
      this.selectedProvinceId = null
      this.selectedCityId = null
      this.selectedDistrictId = null
      this.selectedTagIds = []
      this.selectedBrandId = null
      this.selectedBrandName = ''
      this.filterStatus = ''
      this.filterParams = {}
      this.isFilterMode = false
      this.showFilterDialog = false
      this.fetchGoodsData()
    },
    async confirmFilter () {
      const params = {}
      if (this.selectedProvinceId) params.province_id = this.selectedProvinceId
      if (this.selectedCityId) params.city_id = this.selectedCityId
      if (this.selectedDistrictId) params.dist_id = this.selectedDistrictId
      if (this.selectedTagIds.length) params.label_id = this.selectedTagIds.join(',')
      if (this.selectedBrandId) {
        const brand = this.brandList.find(b => b.id === this.selectedBrandId)
        params.comlist_v3 = brand ? brand.bfenlei4 : ''
      }
      if (this.filterStatus) params.status = this.filterStatus

      this.filterParams = params
      this.isFilterMode = Object.keys(params).length > 0
      this.showFilterDialog = false
      this.fetchGoodsData()
    },

    // ============================
    // 省市区
    // ============================
    async loadProvinceList () {
      try {
        const res = await xcxrequest.post('/gouwu/address.php?a=provinces')
        if (res.code === '0') {
          this.provinceList = res.data
        }
      } catch (error) {
        console.error('加载省份失败', error)
      }
    },
    async onProvinceChange (provinceId) {
      this.selectedCityId = null
      this.selectedDistrictId = null
      this.cityList = []
      this.districtList = []
      if (provinceId) {
        const res = await xcxrequest.post('/gouwu/address.php?a=cities', { province_id: provinceId })
        if (res.code === '0' && res.data) {
          this.cityList = res.data
        }
      }
    },
    async onCityChange (cityId) {
      this.selectedDistrictId = null
      this.districtList = []
      if (cityId) {
        const res = await xcxrequest.post('/gouwu/address.php?a=districts', { city_id: cityId })
        if (res.code === '0' && res.data) {
          this.districtList = res.data
        }
      }
    },

    // ============================
    // 标签 & 品牌
    // ============================
    async loadTagsAndBrands () {
      try {
        const shopId = localStorage.getItem('sh_id') || '2'
        const tagRes = await request.post('/goods/fenzu_jiben_list.php', { bfenlei1: shopId, bfenlei2: '502' })
        if (tagRes.code === 0 && tagRes.fenzuList) {
          this.goodsTypeList = tagRes.fenzuList
        }
        const brandRes = await request.post('/goods/fenzu_jiben_list.php', { bfenlei1: shopId, bfenlei2: '501' })
        if (brandRes.code === 0 && brandRes.fenzuList) {
          this.brandList = brandRes.fenzuList
        }
      } catch (error) {
        console.error('加载标签或品牌失败', error)
      }
    },

    // ============================
    // 商品详情弹窗
    // ============================
    showGoodsDetail (item) {
      this.currentGoods = item
      this.showDetailDialog = true
    },
    closeDetail () {
      this.showDetailDialog = false
    },

    // ============================
    // 规格操作（上下架 / 删除）
    // ============================
    async toggleSpecStatus (spec) {
      try {
        const newStatus = spec.status === '上架' ? '下架' : '上架'
        const userType = localStorage.getItem('user_type') || ''
        const params = { mx_id: spec.id, status: newStatus, user_type: userType }
        const res = await request.post('/goods/goods_status.php', params)
        if (res && res.code === 0) {
          spec.status = newStatus
          this.$message.success(`${newStatus}成功`)
        } else {
          this.$message.error(res.msg || '操作失败')
        }
      } catch (error) {
        console.error('切换规格状态失败', error)
        this.$message.error('操作失败')
      }
    },
    async delSpecStatus (spec) {
      try {
        const num = await this.getkucun(spec.id)
        if (num > 0) {
          this.$message.warning('该规格还有剩余库存，无法删除')
          return
        }
        await this.$confirm(`确定要删除规格 "${spec.comlist_v1}" 吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        const workId = localStorage.getItem('work_id') || ''
        const userId = localStorage.getItem('user_id') || ''
        const params = { mx_id: spec.id, work_id: workId, user_id: userId }
        const res = await request.post('/include/goods_jiben_mxdel.php', params)
        if (res && res.code === 0) {
          this.$message.success('删除成功')
          this.closeDetail()
          this.fetchGroupList().then(() => this.fetchGoodsData())
        } else {
          this.$message.error(res.msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除规格异常', error)
          this.$message.error('删除异常')
        }
      }
    },
    async getkucun (mxId) {
      const res = await request.post('/goods/kucun_display.php?a=kucun', { id: mxId })
      return parseInt(res?.res) || 0
    },

    // ============================
    // 编辑 / 删除 货品
    // ============================
    editGoods (id) {
      this.closeDetail()
      this.formMode = 'edit'
      this.editingGoodsId = id
      this.formDialogVisible = true
    },
    async deleteGoods (id) {
      if (this.currentGoods.com_all > 0) {
        this.$message.warning('该货品还有剩余库存，无法删除')
        return
      }
      try {
        await this.$confirm('确定要删除此货品吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        const workId = localStorage.getItem('work_id') || ''
        const userId = localStorage.getItem('user_id') || ''
        const params = { id, work_id: workId, user_id: userId }
        await request.post('/include/goods_jiben_del.php', params)
        this.$message.success('删除成功')
        this.closeDetail()
        this.fetchGroupList().then(() => this.fetchGoodsData())
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除货品异常', error)
          this.$message.error('删除失败')
        }
      }
    },

    // ============================
    // 新建货品
    // ============================
    handleAdd () {
      this.formMode = 'add'
      this.editingGoodsId = null
      this.formDialogVisible = true
    },

    // ============================
    // 表单提交成功回调
    // ============================
    onFormSuccess () {
      this.fetchGoodsData()
    },

    // ============================
    // 分页事件
    // ============================
    handleSizeChange (val) {
      this.pageSize = val
      this.currentPage = 1
      this.fetchGoodsData(true)
    },
    handleCurrentChange (val) {
      this.currentPage = val
      this.fetchGoodsData(true)
    },

    // ============================
    // 工具方法
    // ============================
    getOnlineStatusText (item) {
      if (item.goods_mx && item.goods_mx.length > 0) {
        const hasOnline = item.goods_mx.some(spec => spec.status === '上架')
        return hasOnline ? '上架' : '下架'
      }
      return item.is_online === 1 ? '上架' : '下架'
    },
    getOnlineStatusClass (item) {
      return this.getOnlineStatusText(item) === '上架' ? 'online-status' : 'offline-status'
    },
    getSpecTypeText (item) {
      return item.goods_mx?.length > 1 ? '多规格' : '单规格'
    },
    getSpecTypeClass (item) {
      return item.goods_mx?.length > 1 ? 'multi-spec' : 'single-spec'
    }
  }
}
</script>

<style scoped>
.goods-container {
  padding: 0 20px;
  background-color: #f0f2f6;
  height: 85vh;
  box-sizing: border-box;
  overflow: auto;
}

.filter-card {
  margin-bottom: 16px;
}
.filter-card .filter-form {
  margin-bottom: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
}
.filter-card .filter-form .el-form-item {
  margin-bottom: 0;
}

.table-card {
  min-height: 60vh;
}
.table-card .pagination {
  margin-top: 20px;
  text-align: right;
}

/* 表格内图片占位 */
.el-icon-picture-outline {
  font-size: 32px;
  color: #ccc;
}

/* 详情弹窗样式 */
.detail-header .detail-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #333;
}
.detail-body .label {
  color: #666;
  font-weight: 500;
}
.spec-section {
  margin-top: 20px;
}
.spec-section h4 {
  margin: 0 0 12px 0;
  color: #333;
}
.no-spec {
  color: #999;
  text-align: center;
  padding: 20px 0;
}
.el-dialog .dialog-footer {
  text-align: right;
}
</style>
